import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, HelpCircle, MapPin } from "lucide-react";
import { SERVICE_CATEGORIES } from "../data/content";
import { SERVICE_LONG_CONTENT } from "../data/serviceContent";
import { SERVICE_FAQS, SERVICE_SEO } from "../data/seoContent";
import { SERVICE_LOCATION_LEADS } from "../data/locationContent";
import SEO, { breadcrumbSchema, serviceSchema, faqSchema } from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import ContactForm from "../components/ContactForm";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICE_CATEGORIES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const longContent = SERVICE_LONG_CONTENT[slug];
  const faqs = SERVICE_FAQS[slug] || [];
  const seo = SERVICE_SEO[slug];
  const otherServices = SERVICE_CATEGORIES.filter((s) => s.slug !== slug);

  const path = `/services/${slug}`;
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: service.name, path },
  ];

  const schemas = [
    breadcrumbSchema(breadcrumbs),
    serviceSchema({
      name: seo?.h1Pattern || `${service.name} — ${service.brand}`,
      description: seo?.description || `${service.summary} Legislation: ${service.legislation}.`,
      path,
      serviceType: seo?.h1Pattern,
      // Each itemized service line becomes an Offer in the OfferCatalog — gives
      // Google/Perplexity an explicit list of sub-services to cite.
      offers: (service.items || []).map((i) => i.name),
    }),
  ];
  if (faqs.length) schemas.push(faqSchema(faqs));

  return (
    <div data-testid={`page-service-${slug}`}>
      <SEO
        title={seo?.title || `${service.name} | ${service.brand} · Oxford Environmental Consultants`}
        description={seo?.description || `${service.summary} Legislation: ${service.legislation}.`}
        keywords={seo?.keywords}
        path={path}
        schema={schemas}
      />

      <section className="bg-[#0A0F1C] text-white pt-16 pb-20 lg:pt-24 lg:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-10">
            <Breadcrumb items={breadcrumbs} theme="dark" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">Services · {service.brand}</p>
              <h1 className="font-display text-5xl lg:text-7xl font-bold tracking-tight leading-[1.05]">{service.name}</h1>
              <p className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed">{service.summary}</p>
            </div>
            <div className="lg:col-span-4 border-l-2 border-[#3FBAB0] pl-6">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">Applicable legislation</p>
              <p className="font-display text-lg text-white font-semibold leading-snug">{service.legislation}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {SERVICE_LOCATION_LEADS[slug] && (
            <div className="max-w-4xl mb-10 bg-[#F8FAFC] border-l-2 border-[#3FBAB0] p-6 flex gap-4" data-testid={`service-location-lead-${slug}`}>
              <MapPin className="w-5 h-5 text-[#1FA59A] flex-shrink-0 mt-0.5" />
              <p className="text-base text-slate-700 leading-relaxed">
                {SERVICE_LOCATION_LEADS[slug]} <Link to="/coverage" className="text-[#1FA59A] font-semibold hover:underline">See full coverage →</Link>
              </p>
            </div>
          )}
          {service.hero_copy && (
            <div className="max-w-4xl mb-16 pb-16 border-b border-slate-200">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Approach</p>
              <p className="text-2xl lg:text-3xl font-display text-slate-900 leading-relaxed">{service.hero_copy}</p>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-4">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Scope</p>
              <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Included in this service</h2>
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                {service.items.map((it, i) => (
                  <div key={i} className="bg-white p-6 min-h-[150px]">
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#3FBAB0] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                      <div>
                        <p className="font-display text-lg font-semibold text-slate-900 mb-2">{it.name}</p>
                        <p className="text-slate-600 text-sm leading-relaxed">{it.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            <div className="bg-[#F8FAFC] p-8">
              <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Deliverables</p>
              <ul className="text-slate-800 space-y-2 text-sm leading-relaxed">
                <li>— Technical report, photos &amp; registers</li>
                <li>— Risk scoring &amp; prioritised action plan</li>
                <li>— Compliance certificates where applicable</li>
                <li>— Client portal access (on request)</li>
              </ul>
            </div>
            <div className="bg-[#F8FAFC] p-8">
              <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Methodology</p>
              <ul className="text-slate-800 space-y-2 text-sm leading-relaxed">
                <li>— Pre-site desktop review</li>
                <li>— On-site survey by qualified consultant</li>
                <li>— Technical QA peer review</li>
                <li>— ISO 9001-aligned reporting</li>
              </ul>
            </div>
            <div className="bg-[#F8FAFC] p-8">
              <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Turnaround</p>
              <ul className="text-slate-800 space-y-2 text-sm leading-relaxed">
                <li>— Standard: 10 working days</li>
                <li>— Priority: 5 working days</li>
                <li>— Emergency: 48 hours</li>
                <li>— Programme delivery: rolling</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {longContent && (
        <section className="bg-[#F8FAFC] border-y border-slate-200 py-20 lg:py-28" data-testid="service-long-content">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Technical briefing</p>
                <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">The full picture on {service.name.toLowerCase()}.</h2>
                <p className="mt-6 text-slate-600 leading-relaxed">A detailed overview of the regulatory landscape, our methodology and what duty holders should expect from a competent consultancy.</p>
                <div className="mt-8 border-t border-slate-300 pt-6">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">Core legislation &amp; guidance</p>
                  <p className="font-display text-base text-slate-900 font-semibold leading-snug">{service.legislation}</p>
                </div>
              </div>
            </aside>
            <article className="lg:col-span-8">
              <div className="prose prose-slate max-w-none
                prose-headings:font-display prose-headings:tracking-tight prose-headings:text-slate-900
                prose-h3:text-2xl prose-h3:font-semibold prose-h3:mt-12 prose-h3:mb-4 first:prose-h3:mt-0
                prose-p:text-slate-700 prose-p:leading-[1.75] prose-p:text-base lg:prose-p:text-[17px]
                prose-strong:text-slate-900 prose-strong:font-semibold
                prose-ul:text-slate-700 prose-li:my-1
                [&_section]:mb-2"
                dangerouslySetInnerHTML={{ __html: longContent }} />
            </article>
          </div>
        </section>
      )}

      {faqs.length > 0 && (
        <section className="bg-white py-20 lg:py-28 border-b border-slate-200" data-testid="service-faq">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Frequently asked</p>
              <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">Answers for duty holders.</h2>
              <p className="mt-5 text-slate-600 leading-relaxed">The questions property managers, landlords and duty holders most often ask our consultants about {service.name.toLowerCase()}.</p>
            </div>
            <div className="lg:col-span-8 divide-y divide-slate-200 border-y border-slate-200">
              {faqs.map((f, i) => (
                <details key={i} className="group py-6" data-testid={`faq-${i}`}>
                  <summary className="flex items-start gap-4 cursor-pointer list-none">
                    <HelpCircle className="w-5 h-5 text-[#1FA59A] mt-1 flex-shrink-0" strokeWidth={1.75} />
                    <span className="font-display text-lg lg:text-xl font-semibold text-slate-900 flex-1">{f.q}</span>
                    <span className="text-slate-400 group-open:rotate-45 transition-transform text-2xl leading-none select-none">+</span>
                  </summary>
                  <div className="mt-4 pl-9 text-slate-700 leading-[1.75] text-base" dangerouslySetInnerHTML={{ __html: f.a }} />
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#F8FAFC] py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#1FA59A] font-semibold mb-4">Free no-obligation quote · 24-hour response</p>
            <h2 className="font-display text-4xl font-bold text-slate-900 tracking-tight">Get your {service.name.toLowerCase()} compliance scoped today.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Share a few details and an OEC expert will respond with a fixed-fee proposal within one working day. No call centres, no chasing — direct line to the {service.brand} team.</p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><span className="text-[#3FBAB0] font-bold">✓</span> Fixed-price proposal within 48 hours</li>
              <li className="flex gap-2"><span className="text-[#3FBAB0] font-bold">✓</span> Audit-ready reporting included</li>
              <li className="flex gap-2"><span className="text-[#3FBAB0] font-bold">✓</span> ISO-accredited delivery</li>
              <li className="flex gap-2"><span className="text-[#3FBAB0] font-bold">✓</span> No-obligation site visit available</li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <ContactForm defaultService={service.name} />
          </div>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="related-services">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">Also from OEC</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-10 max-w-3xl">Other UK property compliance services delivered under one ISO-aligned governance framework.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 bg-white border border-slate-200 divide-x divide-y divide-slate-200 md:divide-y-0 lg:divide-y-0">
            {otherServices.map((s) => {
              const sSeo = SERVICE_SEO[s.slug];
              const anchorText = sSeo?.h1Pattern || s.name;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="group bg-white p-6 hover:bg-[#0A0F1C] hover:text-white transition-colors border-t border-slate-200 first:border-t-0 md:border-t-0" data-testid={`related-service-${s.slug}`} title={`Read about ${anchorText.toLowerCase()}`}>
                  <div className="flex justify-between items-start mb-4">
                    <p className="font-display text-lg font-semibold">{anchorText}</p>
                    <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-sm text-slate-500 group-hover:text-white/60">{s.brand} · {s.legislation?.split(",")[0]}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
